import {pickDefined} from '@benchling/js-utils';

import {AnalysisGraphQL, AnalysisViewGraphQL, AnalysisViewLineageNode} from 'analysis-tool/AnalysisTypes';
import {ANALYSIS_VISUALIZATION_TYPE_TO_ICON} from 'analysis-tool/charting/AnalysisChartingConstants';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {getAnalysisActionHistory} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  ViewReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  SIDEBAR_FAILED_ICON,
  SIDEBAR_INACTIVE_ICON,
  SIDEBAR_SPINNER_ICON,
} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PipelineStepRawData,
  pipelineStepIsStepType,
  pipelineStepRawDataIsKind,
} from 'pipelines/PipelineTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

interface SummarizedView {
  lineageLength: number;
  lastNode: {
    stepId: string;
    stepType: PipelineStepType;
    statusCode: PipelineStepStatusCode;
    hasStepOutputReference: boolean;
    rawDataIndex?: number | null;
    stepDataName?: string | undefined;
  } | null;
}

/**
 * Summarize view information for asserts.
 *
 * @package
 */
export function summarizeView(view: AnalysisViewGraphQL): SummarizedView {
  const lastNode = view.lineage[view.lineage.length - 1];
  return {
    lineageLength: view.lineage.length,
    lastNode: lastNode
      ? {
          stepId: lastNode.step.id,
          stepType: lastNode.step.stepType,
          statusCode: lastNode.step.statusCode,
          hasStepOutputReference: lastNode.stepOutputReference != null,
          rawDataIndex: lastNode.stepOutputReference?.rawDataIndex,
          stepDataName: lastNode.stepOutputReference?.outputStepData?.name,
        }
      : null,
  };
}

/**
 * Assert that a view exists for the given view reference, with detailed debugging context.
 *
 * @returns The view (type-narrowed to non-null)
 */
export function assertViewExists(
  view: AnalysisViewGraphQL | null,
  analysis: AnalysisGraphQL,
  viewReference: ViewReference
): asserts view is AnalysisViewGraphQL {
  assert(view != null, 'Expected view to exist', {
    viewReference,
    analysisId: analysis.id,
    viewCount: analysis.views.length,
    views: analysis.views.map((view) => summarizeView(view)),
    recentActions: getAnalysisActionHistory(),
  });
}

/**
 * Return the child views for a given view.
 *
 * A child view is a view whose first node's step's incoming edge is from the last node of this view.
 */
export function getChildViewsForView(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL
): Array<AnalysisViewGraphQL> {
  if (view.lineage.length < 1) {
    return [];
  }

  const lastNode = view.lineage[view.lineage.length - 1];
  const step = lastNode.step;
  const stepOutputReference = lastNode.stepOutputReference;

  if (stepOutputReference == null) {
    return [];
  }

  return analysis.views.filter((otherView) => {
    const firstStep = otherView.lineage[0].step;
    return firstStep.incomingEdges.some(
      (incomingEdge) =>
        incomingEdge.fromStep.id === step.id &&
        incomingEdge.fromStepData.id === stepOutputReference.outputStepData.id &&
        (incomingEdge.fromStepDataIndex === stepOutputReference.rawDataIndex ||
          // Mimics similar logic in get_views.py; if the edge has an index set but the steps aren't in a completed state, we still want to match
          (stepOutputReference.rawDataIndex == null && incomingEdge.fromStepDataIndex === 0))
    );
  });
}

/**
 * Return the parent views for a given view.
 *
 * A parent view is a view whose last node's step has an outgoing edge to any step in this view.
 */
export function getParentViewsForView(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL
): Array<AnalysisViewGraphQL> {
  const viewStepIds = new Set(view.lineage.map((lineageNode) => lineageNode.step.id));
  if (viewStepIds.size === 0) {
    return [];
  }

  return analysis.views.filter((otherView) => {
    if (otherView.lineage.length === 0) {
      return false;
    }

    const lastStep = otherView.lineage[otherView.lineage.length - 1].step;
    return lastStep.outgoingEdges.some((outgoingEdge) => viewStepIds.has(outgoingEdge.toStep.id));
  });
}

/**
 * Return if all parent views have succeeded, i.e. their last lineage node is succeeded.
 */
export function allParentViewsSucceeded(analysis: AnalysisGraphQL, view: AnalysisViewGraphQL): boolean {
  return getParentViewsForView(analysis, view).every(
    (parentView) =>
      parentView.lineage[parentView.lineage.length - 1].step.statusCode === PipelineStepStatusCode.SUCCEEDED
  );
}

/**
 * Get the output raw data for a view.
 *
 * Returns null if the view does not have an output data, which may occur for situations like:
 *  - The final step of the view hasn't been successfully run yet
 *  - The final step of the view doesn't produce outputs, e.g. the SEND_TO_DOCUMENT step
 */
export function getOutputDataForView(view: AnalysisViewGraphQL): PipelineStepRawData | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (
    lastNode == null ||
    lastNode.stepOutputReference == null ||
    lastNode.stepOutputReference.rawDataIndex == null
  ) {
    return null;
  }

  return lastNode.stepOutputReference.outputStepData.rawDatas[lastNode.stepOutputReference.rawDataIndex];
}

/**
 * Return true if the view outputs a data frame.
 */
export function isDataFrameView(view: AnalysisViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.DATASET);
}

/**
 * Return true if the view outputs a chart.
 */
export function isChartView(view: AnalysisViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.CHART);
}

/**
 * Return true if the view outputs a file.
 */
export function isFileView(view: AnalysisViewGraphQL): boolean {
  const outputData = getOutputDataForView(view);
  return outputData != null && pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.FILE);
}

/** Get the icon for a given view.
 *
 * Icons are based on the last node's status and the type of raw data it contains.
 * - Layout icons are either specified by their config or the default layout icon
 * - Succeeded views' icons are based on the raw data's type
 * - Other views' icons are based on their status
 */
export function getIconForView(analysis: AnalysisGraphQL, view: AnalysisViewGraphQL): BenchlingIconName {
  const lastNode = view.lineage[view.lineage.length - 1];

  if (pipelineStepIsStepType(PipelineStepType.LAYOUT, 0, lastNode.step)) {
    return (lastNode.step.config?.icon as BenchlingIconName) ?? 'chart-tree-map';
  } else if (lastNode.step.statusCode === PipelineStepStatusCode.FAILED) {
    return allParentViewsSucceeded(analysis, view) ? SIDEBAR_FAILED_ICON : SIDEBAR_INACTIVE_ICON;
  } else if (
    lastNode.step.statusCode === PipelineStepStatusCode.BLOCKED ||
    lastNode.step.statusCode === PipelineStepStatusCode.RUNNING ||
    lastNode.step.statusCode === PipelineStepStatusCode.WAITING
  ) {
    return SIDEBAR_SPINNER_ICON;
  } else if (lastNode.step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const outputData = getOutputDataForView(view);

    // TODO(BNCH-136219): This should never happen in practice, but there's a possible race condition making
    // it possible for the front-end to have the wrong state loaded temporarily. This will avoid a crash of
    // sidebar, and gather additional data on this issue.
    if (outputData == null) {
      logging.error('Unexpectedly got a SUCCEEDED step with no output data', {
        extra: {
          viewInfo: view.lineage.map(({step, stepOutputReference}) => ({
            stepInfo: pickDefined(step, 'id', 'stepType', 'statusCode', 'statusMessage', 'config'),
            stepOutputReference,
          })),
        },
      });
      return SIDEBAR_SPINNER_ICON;
    }

    if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.CHART)) {
      if (pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, lastNode.step)) {
        return ANALYSIS_VISUALIZATION_TYPE_TO_ICON[lastNode.step.config!.chartConfig.type];
      } else if (pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, lastNode.step)) {
        return ANALYSIS_VISUALIZATION_TYPE_TO_ICON[AnalysisVisualizationType.SCATTER_PLOT];
      } else {
        return ModelTypeConstants.CHART;
      }
    } else if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.DATASET)) {
      return ModelTypeConstants.DATA_FRAME;
    } else if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.FILE)) {
      return ModelTypeConstants.FILE;
    } else {
      assertAndContinue(false, 'Unsupported raw data kind', {rawData: assertNever(outputData)});
    }
  } else {
    assertAndContinue(false, 'Unsupported status', {status: assertNever(lastNode.step.statusCode)});
  }

  return 'question';
}

/**
 * Return the client id for a given view.
 *
 * This function makes a best attempt at giving IDs to views. There are some major caveats.
 *
 * ### Issue with view ids
 *
 * Views fundamentally don't have an identity because they're data structures built on top of steps by the
 * backend. This means you can't access a view outside a given session, and they don't have stable IDs.
 *
 * ### View IDs
 *
 * Given the lack of identity, we still make a best attempt at composing an ID for views with this function.
 * We rely on the fact that the last node of a view can only be in 1 view ever. i.e., we never render the
 * same visual "view" twice in the UI.
 *
 * This means we can define the ID as the step id + the step output id (when present) + the raw data id (when present).
 *
 * ### Alternatives
 *
 * The best alternative would be to use the raw data index instead of the raw data id, which could be useful in some states,
 * but it seemed more reasonable to store state against a specific raw data. This is a trade-off that can
 * be revisited in the future.
 *
 * The other alternative is just storing the raw data id, but that's not always available.
 *
 * ### Caveats
 *
 * This ID has some weaknesses, but another way of thinking about it is that these cases are different views:
 * - It changes when the output of the last step in a view changes. This means it's good at storing state
 * against specific data but not a view.
 * - If we add a step at the end of a view, the id changes.
 *
 * If these caveats are unacceptable for your use case, you can use a different method generating the an ID
 * for a view.
 */
export function getDefaultClientIdForView(analysisView: AnalysisViewGraphQL): string {
  if (!assertAndContinue(analysisView.lineage.length > 0, 'Lineage must have at least one node')) {
    return 'DEFAULT_CLIENT_ID';
  }

  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference;

  const idPart1 = lastNode.step.id;
  const idPart2 = stepOutputReference == null ? '' : `-${stepOutputReference.outputStepData.id}`;
  const rawDataIndex = stepOutputReference?.rawDataIndex;
  const idPart3 =
    stepOutputReference == null ||
    rawDataIndex == null ||
    !assertAndContinue(
      rawDataIndex < stepOutputReference.outputStepData.rawDatas.length,
      'rawDataIndex is out of bounds'
    )
      ? ''
      : `-${stepOutputReference.outputStepData.rawDatas[rawDataIndex].id}`;

  return `${idPart1}${idPart2}${idPart3}`;
}

/**
 * Get the last node in the lineage that is successful, or return `null` if there is no successful nodes.
 */
export function getLastSuccessfulNode(view: AnalysisViewGraphQL): AnalysisViewLineageNode | null {
  return view.lineage.findLast(({step}) => step.statusCode === PipelineStepStatusCode.SUCCEEDED) ?? null;
}

/**
 * Get the title for a given lineage node.
 *
 * This function only supports successful nodes. It retrieves the title of a node by looking at the output
 * raw data for that node.
 */
export function getTitleForLineageNode(node: AnalysisViewLineageNode): string {
  if (
    !assertAndContinue(
      node.step.statusCode === PipelineStepStatusCode.SUCCEEDED,
      'Function only supports successful nodes',
      {
        stepStatusCode: node.step.statusCode,
        stepType: node.step.stepType,
      }
    )
  ) {
    return getTitleForStatus(node.step.statusCode);
  }

  const stepOutputReference = node.stepOutputReference;

  if (
    stepOutputReference == null ||
    // Check twice since assertAndContinue does not narrow types
    stepOutputReference.rawDataIndex == null ||
    !assertAndContinue(
      stepOutputReference.rawDataIndex != null,
      'Expected stepOutputReference to have rawDataIndex'
    ) ||
    !assertAndContinue(
      stepOutputReference.rawDataIndex < stepOutputReference.outputStepData.rawDatas.length,
      'rawDataIndex is out of bounds'
    )
  ) {
    return STEP_TYPE_TO_USER_FACING_NAME[node.step.stepType];
  }

  const rawData = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex];

  if (
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.CHART) ||
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.DATASET) ||
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.FILE)
  ) {
    return rawData.name;
  } else {
    throw new Error(`Unknown raw data kind type: ${assertNever(rawData)}`);
  }
}

/**
 * Get the title for a given status.
 *
 * Although this function supports all status codes, it should only be called with non-successful statuses.
 * For successful lineage nodes, use `getTitleForLineageNode` instead.
 */
export function getTitleForStatus(status: PipelineStepStatusCode): string {
  switch (status) {
    case PipelineStepStatusCode.SUCCEEDED:
      assertAndContinue(false, 'getTitleForStatus should not be called with a successful status');
      return 'Succeeded view';
    case PipelineStepStatusCode.FAILED:
      return 'Failed view';
    case PipelineStepStatusCode.BLOCKED:
    case PipelineStepStatusCode.RUNNING:
    case PipelineStepStatusCode.WAITING:
      return 'Running view';
    default:
      throw new Error(`Unknown status: ${status}`);
  }
}

/**
 * Get the title for a given view.
 *
 * This function works as follows:
 *  - If the last node is a chart, table, regression, or layout step, and its config exists, get the title from the
 *  config
 *  - Gets the last successful node in the lineage and returns its title. @see getTitleForLineageNode for details
 *  - If there are no successful nodes, returns the title based on the status of the last node. @see getTitleForStatus for details
 *  - If there are no nodes, throw a warning, and return `Analysis view` as a default.
 */
export function getTitleForView(view: AnalysisViewGraphQL): string {
  const lastStep = view.lineage[view.lineage.length - 1]?.step;
  if (lastStep?.config != null) {
    if (pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, lastStep)) {
      return lastStep.config.chartName;
    } else if (pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, lastStep)) {
      return lastStep.config.viewName;
    } else if (pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, lastStep)) {
      return lastStep.config.name;
    } else if (pipelineStepIsStepType(PipelineStepType.LAYOUT, 0, lastStep)) {
      return lastStep.config.name;
    }
  }

  const lastSuccessfulNode = getLastSuccessfulNode(view);
  if (lastSuccessfulNode != null) {
    return getTitleForLineageNode(lastSuccessfulNode);
  } else if (view.lineage.length > 0) {
    return getTitleForStatus(view.lineage[view.lineage.length - 1].step.statusCode);
  } else {
    assertAndContinue(false, 'Expected views to have at least one lineage node.');
    return 'Analysis view';
  }
}

export function getEditViewRightSidebarItemForLineageNode(
  lineageNode: AnalysisViewLineageNode
): AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW> | null {
  return {
    type: AnalysisRightSidebarItemType.EDIT_VIEW,
    viewReference: {
      stepId: lineageNode.step.id,
      outputReference:
        lineageNode.stepOutputReference != null
          ? {
              stepDataName: lineageNode.stepOutputReference.outputStepData.name,
              rawDataIndex: lineageNode.stepOutputReference.rawDataIndex,
            }
          : null,
    },
  };
}

export function getEditViewRightSidebarItemForView(
  view: AnalysisViewGraphQL
): AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW> | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    return null;
  }
  return getEditViewRightSidebarItemForLineageNode(lastNode);
}

export function getMainViewItemForLineageNode(
  lineageNode: Omit<AnalysisViewLineageNode, '__typename'>
): AnalysisMainViewItem<AnalysisMainViewItemType.DATA_VIEW> {
  return {
    type: AnalysisMainViewItemType.DATA_VIEW,
    viewReference: {
      stepId: lineageNode.step.id,
      outputReference:
        lineageNode.stepOutputReference != null
          ? {
              stepDataName: lineageNode.stepOutputReference.outputStepData.name,
              rawDataIndex: lineageNode.stepOutputReference.rawDataIndex,
            }
          : null,
    },
  };
}

export function getMainViewItemForView(view: AnalysisViewGraphQL): AnalysisMainViewItem {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    return {
      type: AnalysisMainViewItemType.EMPTY_ANALYSIS,
    };
  }

  if (lastNode.step.stepType === PipelineStepType.LAYOUT) {
    return {
      type: AnalysisMainViewItemType.DATA_LAYOUT,
      stepId: lastNode.step.id,
    };
  }

  return getMainViewItemForLineageNode(lastNode);
}

/**
 * Return the view that contains corresponds with the given view reference.
 *
 * A view corresponds when one of its lineage nodes:
 *   1. Uses the same step
 *   2. Has the same step data name
 *   3. Either has the same raw data index, or has a null raw data index
 *
 * A null raw data index indicates a step that hasn't succeeded, which we'll still want to match against.
 */
export function getViewForViewReference(
  analysis: AnalysisGraphQL,
  {stepId, outputReference}: ViewReference
): AnalysisViewGraphQL | null {
  if (outputReference == null) {
    const views = analysis.views.filter(({lineage}) =>
      lineage.some(({step: viewStep}) => viewStep.id === stepId)
    );
    assertAndContinue(
      views.length <= 1,
      'Expected to find at most one view for step when outputReference is null',
      {views, stepId, outputReference}
    );
    return views.length === 1 ? views[0] : null;
  }

  const views = analysis.views.filter(({lineage}) =>
    lineage.some(
      ({step: viewStep, stepOutputReference: viewStepOutputReference}) =>
        viewStep.id === stepId &&
        viewStepOutputReference != null &&
        viewStepOutputReference.outputStepData.name === outputReference.stepDataName &&
        (viewStepOutputReference.rawDataIndex === outputReference.rawDataIndex ||
          // Because analysis.views and viewReferences can get out of sync, if we try to view a 'null' raw data index and
          // there is an otherwise matching view with a non-null raw data index, we default to matching index 0.
          (viewStepOutputReference.rawDataIndex === 0 && outputReference.rawDataIndex == null) ||
          viewStepOutputReference.rawDataIndex == null)
    )
  );

  assertAndContinue(views.length <= 1, 'Expected to find at most one view for stepId and outputReference', {
    views,
    stepId,
    outputReference,
  });
  return views.length === 1 ? views[0] : null;
}

/**
 * Define steps that although they take some input, are considered "top-level" in an analysis instead of being
 * rendered as the child of a view
 */
export const TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES = [
  PipelineStepType.JOIN,
  PipelineStepType.PROMOTE_DATASET,
  PipelineStepType.THIRD_PARTY,
  PipelineStepType.UNION_DATASETS,
  PipelineStepType.LOOK_UP,
  PipelineStepType.USER_DEFINED_FUNCTION,
];

/**
 * Return true if the given view is a top-level view.
 *
 * A top-level view is a view that is not the child of another view or whose first node's step is a step
 * in the `TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES` list.
 *
 * @see TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES for the list of steps that promote a view to top-level.
 */
export function isTopLevelAnalysisView(view: AnalysisViewGraphQL): boolean {
  if (
    !assertAndContinue(view.lineage.length > 0, 'Expected a single step in the lineage of an analysis view')
  ) {
    return view.lineage[0].step == null;
  }

  const firstStep = view.lineage[0].step;

  return (
    firstStep.incomingEdges.length === 0 ||
    (firstStep.incomingEdges.length > 1 &&
      firstStep.stepType !== PipelineStepType.LAYOUT &&
      // TODO(BNCH-140918): Temporary hack to hide one of the files generated by EO, can be removed when the
      // files section is merged with tables
      !firstStep.stepType.startsWith('EXPERIMENT_OPTIMIZATION')) ||
    TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES.includes(firstStep.stepType)
  );
}

/**
 * Return true if the given view is a top-level view and outputs a data frame.
 */
export function isTopLevelDataFrameView(view: AnalysisViewGraphQL): boolean {
  return isTopLevelAnalysisView(view) && isDataFrameView(view);
}

/**
 * Return true if the given view is a top-level view and outputs a file.
 */
export function isTopLevelFileView(view: AnalysisViewGraphQL): boolean {
  return isTopLevelAnalysisView(view) && isFileView(view);
}

/**
 * Steps that were treated as top-level steps in the legacy sidebar.
 * TODO(BNCH-125215): Remove this.
 */
const LEGACY_TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES = [
  PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
  PipelineStepType.SAVE_TO_RESULTS,
  PipelineStepType.SEND_TO_DOCUMENT,
  PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
  PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
  PipelineStepType.PARAMETER_OPTIMIZATION_MODELING,
  PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION,
];
export function isLegacyTopLevelAnalysisView(view: AnalysisViewGraphQL): boolean {
  if (
    !assertAndContinue(view.lineage.length > 0, 'Expected a single step in the lineage of an analysis view')
  ) {
    return view.lineage[0].step == null;
  }

  const firstStep = view.lineage[0].step;

  return LEGACY_TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES.includes(firstStep.stepType);
}
